import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';

const hourlyItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'skill-pay-hourly-compensation',
    title: 'Hourly Compensation',
    info: {
      text: 'The average predicted hourly pay over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'skill-pay-hourly-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'skill-pay-hourly-top-companies',
    title: 'Top Companies',
    info: {
      text: 'The top companies that have hired for this skill. More information on company mapping can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#company-mapping',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      chartType: 'box',
      secondaryDimension: 'company',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'skill-pay-hourly-top-geographies',
    title: 'Top Geographies',
    info: {
      text: 'The most common geographies for this skill.',
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      secondaryDimension: 'geography',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'skill-pay-hourly-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of hourly pay. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['contractor_pay_rate'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const baseItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'skill-pay-base-compensation',
    title: 'Base Compensation',
    info: {
      text: 'The average predicted base salary over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'skill-pay-base-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'skill-pay-base-top-companies',
    title: 'Top Companies',
    info: {
      text: 'The top companies that have hired for this skill. More information on company mapping can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#company-mapping',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      chartType: 'box',
      secondaryDimension: 'company',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'skill-pay-base-top-geographies',
    title: 'Top Geographies',
    info: {
      text: 'The most common geographies for this skill.',
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      secondaryDimension: 'geography',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'skill-pay-base-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of base salary. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['base_salary'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const totalItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'skill-pay-total-compensation',
    title: 'Total Compensation',
    info: {
      text: 'The average predicted total compensation over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'skill-pay-total-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'skill-pay-total-top-companies',
    title: 'Top Companies',
    info: {
      text: 'The top companies that have hired for this skill. More information on company mapping can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#company-mapping',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      chartType: 'box',
      secondaryDimension: 'company',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'skill-pay-total-top-geographies',
    title: 'Top Geographies',
    info: {
      text: 'The most common geographies for this skill.',
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      secondaryDimension: 'geography',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'skill-pay-total-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of total compensation. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['total_compensation'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const sharedConfig: Pick<
  TabContent,
  'grid' | 'primaryFilter' | 'secondaryFilters'
> = {
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.ACTIVITY,
    FilterId.CONTINGENT_WORKERS,
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
  ].map((id) => ({ id })),
  primaryFilter: {
    id: FilterId.CUSTOM_SKILL,
    tagColors: true,
  },
};

const skillPayHourlyTab: TabContent = {
  id: 'skill-pay-hourly',
  items: hourlyItems,
  ...sharedConfig,
};

const skillPayBaseTab: TabContent = {
  id: 'skill-pay-base',
  items: baseItems,
  ...sharedConfig,
};

const skillPayTotalTab: TabContent = {
  id: 'skill-pay-total',
  items: totalItems,
  ...sharedConfig,
};

export const skillPay: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.SKILL_PAY,
  revelioPageId: RevelioPageId.SKILL_PAY,
  name: 'Compensation',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'skill-pay-hourly',
      label: 'Hourly',
      tabContent: skillPayHourlyTab,
      isActive: false,
    },
    {
      id: 'skill-pay-base',
      label: 'Base',
      tabContent: skillPayBaseTab,
      isActive: false,
    },
    {
      id: 'skill-pay-total',
      label: 'Total',
      tabContent: skillPayTotalTab,
      isActive: true,
    },
  ],
};
